import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useSectionTracking } from "./AnalyticsProvider";

const reasons = [
  {
    number: "01",
    title: "A Gübelin First — and Only",
    detail: "In 170 years of operation, Gübelin Gem Lab has never produced a Premium Gemological Profile for a raw emerald. They did it once. For Bridgette. There will not be a second.",
  },
  {
    number: "02",
    title: "Muzo Origin, 1,055 Carats, Untreated",
    detail: "From the most legendary emerald source on Earth. No clarity enhancement. No human alteration. Gübelin certified. A specimen of this size, origin, and purity has not appeared in recorded history.",
  },
  {
    number: "03",
    title: "Private Ownership of a Museum-Grade Specimen",
    detail: "The Duke of Devonshire and the Patricia Emerald sit behind glass in London and New York — permanently. Bridgette is the only specimen of this calibre available to a private collector. Ownership transfers not just the object, but a place in gemological history.",
  },
  {
    number: "04",
    title: "30 Million Years in the Making",
    detail: "Bridgette was formed deep within the Colombian Andes before the first humans walked the Earth. She is not a product. She is a geological event — complete, unaltered, and unrepeatable.",
  },
];

export default function ElevatorPitch() {
  const sectionRef = useSectionTracking("elevator_pitch");
  const animRef = useRef(null);
  const inView = useInView(animRef, { once: true, margin: "-80px" });

  return (
    <section ref={sectionRef} className="relative py-14 lg:py-20 overflow-hidden" data-testid="elevator-pitch-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#111110] via-[#14130F] to-[#111110]" />

      <div className="max-w-5xl mx-auto px-6 lg:px-12 relative z-10" ref={animRef}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-8 lg:mb-14"
        >
          <p className="overline mb-6">Why Bridgette</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F0EA] font-light tracking-tighter leading-tight max-w-3xl mx-auto">
            Four reasons this will never{" "}
            <span className="italic text-[#C5A059]">happen again</span>
          </h2>
        </motion.div>

        <div className="space-y-0 border-t border-[#C5A059]/15">
          {reasons.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.12 }}
              className="grid grid-cols-12 gap-4 lg:gap-8 py-8 lg:py-10 border-b border-[#C5A059]/10 items-start"
              data-testid={`pitch-reason-${i}`}
            >
              <div className="col-span-2 lg:col-span-1">
                <span className="font-serif text-2xl lg:text-3xl text-[#C5A059]/30">{r.number}</span>
              </div>
              <div className="col-span-10 lg:col-span-4">
                <h3 className="font-serif text-xl lg:text-2xl text-[#F4F0EA] leading-snug font-normal">{r.title}</h3>
              </div>
              <div className="col-span-10 col-start-3 lg:col-span-7 lg:col-start-6">
                <p className="text-base text-[#B8B3AA]/80 leading-relaxed">{r.detail}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-8 lg:mt-14 text-center"
        >
          <p className="font-serif text-2xl sm:text-3xl lg:text-4xl italic text-[#C5A059]/60 max-w-2xl mx-auto leading-relaxed">
            The next owner of Bridgette will possess something that no other collector on Earth can claim.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
